import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { Button, Typography, makeStyles } from "@material-ui/core";
import SignalWifiOffIcon from "@material-ui/icons/SignalWifiOff";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { formatDateTime, localTimeZone, toDate } from "../../utils/waitingTime";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, padding: "12px 24px",
    color: "#FFFFFF", background: "linear-gradient(110deg, #B91C1C, #991B1B)",
    borderTop: "1px solid rgba(255,255,255,.14)",
    [theme.breakpoints.down("xs")]: { padding: "10px 12px", gap: 10 },
  },
  icon: { flexShrink: 0, fontSize: 24, alignSelf: "flex-start", marginTop: 2 },
  content: { flex: "1 1 260px", minWidth: 0 },
  message: { fontSize: ".81rem", fontWeight: 700, lineHeight: 1.5, color: "inherit" },
  detail: { display: "block", fontSize: ".7rem", lineHeight: 1.5, marginTop: 3, color: "#FEE2E2", overflowWrap: "anywhere" },
  action: {
    flexShrink: 0, color: "#991B1B", backgroundColor: "#FFFFFF", fontSize: ".73rem", fontWeight: 800,
    padding: "7px 12px", borderRadius: 8, boxShadow: "none", textTransform: "none",
    "&:hover": { backgroundColor: "#FEF2F2", boxShadow: "none" },
    "&.Mui-focusVisible": { outline: "2px solid #FFFFFF", outlineOffset: 3 },
    [theme.breakpoints.down("xs")]: { marginLeft: 34 },
  },
}));

export const getOfflineConnections = (connections, user) => (Array.isArray(connections) ? connections : []).filter(connection =>
  connection && connection.isTest !== true && Number(connection.isTest) !== 1 && connection.status !== "CONNECTED"
  && (!Object.prototype.hasOwnProperty.call(connection, "companyId") || String(connection.companyId) === String(user?.companyId))
);

export default function ConnectionOfflineBanner() {
  const classes = useStyles();
  const { user, isAuth, loading: authLoading } = useContext(AuthContext);
  const { whatsApps, loading } = useContext(WhatsAppsContext);
  if (!isAuth || authLoading || !user?.id || (loading && !whatsApps?.length)) return null;
  const offline = getOfflineConnections(whatsApps, user);
  if (!offline.length) return null;
  const admin = Boolean(user.super || user.profile === "admin");
  const names = offline.map(connection => connection.name || "WhatsApp #" + connection.id);
  const namesLabel = names.slice(0, 2).join(", ") + (names.length > 2 ? " e mais " + (names.length - 2) : "");
  const recorded = offline.map(connection => ({ date: toDate(connection.disconnectedSince), estimated: connection.disconnectedSinceEstimated })).filter(item => item.date).sort((left, right) => left.date - right.date);
  const since = recorded[0];
  const reconnecting = offline.every(connection => connection.status === "OPENING" || connection.status === "PAIRING");
  return <section className={classes.root} role="alert" aria-atomic="true" data-testid="connection-offline-banner">
    <SignalWifiOffIcon className={classes.icon} aria-hidden="true" />
    <div className={classes.content}>
      <Typography component="p" className={classes.message}>{admin
        ? "O WhatsApp está desconectado. Conecte novamente para retomar os atendimentos."
        : "O WhatsApp está desconectado. Entre em contato com o administrador da sua empresa."}</Typography>
      <span className={classes.detail}>
        <span title={names.join(", ")}>{offline.length > 1 ? offline.length + " conexões: " : "Conexão: "}{namesLabel}</span>
        {since && <> · {offline.length > 1 ? "Primeiro registro: " : since.estimated ? "Observada desde " : "Desde "}<time dateTime={since.date.toISOString()} title={formatDateTime(since.date) + " · " + localTimeZone()}>{since.date.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</time></>}
        {reconnecting && <> · Reconexão em andamento</>}
      </span>
    </div>
    {admin && <Button component={Link} to="/connections" className={classes.action} variant="contained" disableElevation>Reconectar WhatsApp</Button>}
  </section>;
}
